import { defineStore } from 'pinia'
import userApi from '@/api/userApi'
import { store } from '@/store'
import type { userType } from '@/types/userStoreTypes'
import { CommonUtils, getBenefitValue } from '@/utils/commonUtils'
import { removeToken, setToken, sessionKey } from '@/utils/auth'
import { Code } from '@/config/httpCode'
import type { UserResult } from '@/types/apiTypes'
import { useSystemConfigStoreHook } from '@/store/modules/systemConfig'
import { storageLocal } from '@pureadmin/utils'
import router from '@/router'
import type { ReqRegisterByAccount, ReqBindAccount } from '@/api/type/params'
import integralApi from '@/api/integralApi'
import { UserBenefitType } from '@/types/constantsEnum'
import { UserGender } from '@/config/constants'
import { useMusicPlayerStoreHook } from '@/store/modules/musicPlayer'
import { useWebSocket } from '@/utils/websocket'
// 获取初始用户信息函数
function getInitialUserState(): userType {
  const userInfo = CommonUtils.getUserInfo()
  return {
    id: userInfo?.id ?? 0,
    avatar: userInfo?.avatar ?? '',
    nickname: userInfo?.nickname ?? '',
    extra: userInfo?.extra ?? '',
    inviteCode: userInfo?.inviteCode ?? '',
    username: userInfo?.username ?? '',
    roles: userInfo?.roles ?? [],
    tag: userInfo?.tag ?? '',
    token: userInfo?.token ?? '',
    type: userInfo?.type ?? 1,
    gender: userInfo?.gender ?? UserGender.unknown,
    isLogin: (userInfo?.id ?? 0) > 0,
    signature: userInfo?.signature ?? '',
    scoreDetail: userInfo?.scoreDetail ?? {
      credit: 0,
    },
  }
}
export const useUserStore = defineStore({
  id: 'storeUser',
  state: (): userType => getInitialUserState(),
  actions: {
    // 通用的更新本地存储方法
    updateLocalStorage<K extends keyof userType>(key: K, value: userType[K]) {
      const userInfo = storageLocal().getItem<userType>(sessionKey) || getInitialUserState()
      if (userInfo) {
        userInfo[key] = value
        storageLocal().setItem(sessionKey, userInfo)
      }
    },

    // SET 方法 设置对应用户信息 传入key和value
    setUserData<K extends keyof userType>(key: K, value: userType[K]) {
      this[key] = value
      this.updateLocalStorage(key, value)
    },

    // 密码登录
    async loginByPassword(data) {
      return new Promise<UserResult>((resolve, reject) => {
        userApi
          .loginByPassword(data)
          .then((res) => {
            if (res.statusCode === Code.SUCCESS.statusCode) {
              setToken(res.data)
              // router.push('/')
            }
            resolve(res)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
    // 验证码登录
    async loginByCode(data) {
      return new Promise<UserResult>((resolve, reject) => {
        userApi
          .loginByCode(data)
          .then((res) => {
            if (res.statusCode === Code.SUCCESS.statusCode) {
              setToken(res.data)
              // router.push('/')
            }
            resolve(res)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
    // 清理用户信息
    async clearUserInfo() {
      // 动态导入 WebSocket store 避免循环依赖

      removeToken()
      storageLocal().removeItem(sessionKey)
      const initialState = getInitialUserState()
      Object.keys(initialState).forEach((key) => {
        this[key] = initialState[key]
      })
      useMusicPlayerStoreHook().resetPlayer()
      // 跳转到登录页
      const { close } = useWebSocket()
      close()
      router.push('/login')
    },

    // 注册
    async registerByAccount(data: ReqRegisterByAccount) {
      return new Promise<UserResult>((resolve, reject) => {
        userApi
          .registerByAccount(data)
          .then((res) => {
            if (res && res.statusCode === Code.SUCCESS.statusCode) {
              setToken(res.data)
            }
            resolve(res)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
    /** 微信登陆 */
    async bindAccount(data: ReqBindAccount) {
      return new Promise<UserResult>((resolve, reject) => {
        userApi
          .bindAccount(data)
          .then((res) => {
            if (res.statusCode === Code.SUCCESS.statusCode) {
              setToken(res.data)
            }
            resolve(res)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },
    // 前端登出（需要调用接口）
    async logOut() {
      try {
        await userApi.logout()
        this.clearUserInfo()
      } catch (error) {
        console.error('登出失败:', error)
        // 即使接口失败也清除本地状态
        this.clearUserInfo()
      }
    },

    // 更新积分
    async updateScore() {
      try {
        const integareInfo = await integralApi.getUserBenefit()
        if (integareInfo.statusCode === Code.SUCCESS.statusCode) {
          const creditValue = getBenefitValue(integareInfo.data, UserBenefitType.CREDIT)
          const scoreDetail = {
            credit: creditValue,
          }
          // 更新 store
          this.setUserData('scoreDetail', scoreDetail)
        }
      } catch (error) {
        console.error('更新积分失败:', error)
      }
    },
  },
})

export function useUserStoreHook() {
  return useUserStore(store)
}
